// CSS
import './Chat.scss'

// React and Third Party
import { useState, useEffect, useReducer, useCallback, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import {
  Container,
  Button,
  Form,
  Row,
  Col,
  Spinner,
  InputGroup,
  Alert,
  Modal
} from 'react-bootstrap'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useMediaQuery } from 'react-responsive'
import Drawer from 'react-bottom-drawer'

// Utils
import { Buffer } from 'buffer'
import CryptoJS from 'crypto-js'
import cn from 'classnames'
import parse from 'html-react-parser'

// Assets
import RembleAvatar from '../../../assets/images/Remble_Logomark-Green-25pGreen.png'
import RembleAvatarGolden from '../../../assets/images/Remble_Logomark-Black-Goldenrod.png'

// Components
import { getChatEventSource } from 'components/common/api/Chat'
import { getAuthenticatedUser } from 'components/auth'
import {
  EVENT_ACTION_CHAT,
  EVENT_ACTION_PAGEVIEW,
  EVENT_CATEGORY_CHAT,
  saveUserEvent
} from 'components/common/api/UserEvents'
import { SSE_EVENT_TYPE_MESSAGE, STREAM_COMPLETE } from 'components/common/api'
import { LMSKeys, generativeFeatures } from 'components/common/api/constants'
import ResponseRating from 'components/feedback/ResponseRating/ResponseRating'
import { ROUTE_TODAY } from 'components/common/helpers/Paths'
import useUserHasAcceptedAITOS from 'components/common/hooks/useUserHasAcceptedTOS'
import Loading from 'templates/Loading/Loading'
import Terms from '../Terms/Terms'
import { getTermOrSearchResults } from 'components/common/api/Search'
import ChatSearchExpansion from '../ChatSearchExpansion/ChatSearchExpansion'

// Constants
const chatMinLength = 2
const messageDelay = 1 //2000 // Artificial delay to simulate typing

/**
 * Helper function to handle response state
 * @param {Object} state The current state
 * @param {Object} action The action to perform
 * @returns {Object} The new state
 */
function responseReducer(state, action) {
  switch (action.event) {
    case SSE_EVENT_TYPE_MESSAGE:
      return [...state, action.data]
    case 'reset':
      return []
    default:
      return state
  }
}

// Latest text response and last request
let _latestTextResponse = ''
let _lastRequest = ''

// Last full text response
// let _lastFullTextResponse = ''

// List of chat messages
let _chatMessages = []

// Queue size
let _queueSize = 1

// Event Source
let _eventSource = null

// Event Source Timeout
let _eventSourceTimeout = null

// Event Source Timeout Delay
const _eventSourceTimeoutDelay = 10000

/**
 * Core Chat Component
 * @returns {JSX.Element} The Chat Component
 */
export default function Chat({ setReturnPath, setHeaderTitle }) {
  const location = useLocation()
  const user = getAuthenticatedUser()
  const {
    loading: termsLoading,
    accepted: termsAccepted,
    setAccepted: setTermsAccepted
  } = useUserHasAcceptedAITOS()
  const isLargeScreen = useMediaQuery({ minWidth: 768 })

  const _user_name = useCallback(
    (defaultName = '') =>
      user?.display_name ? user.display_name : defaultName,
    [user?.display_name]
  )

  // Get the generative features
  const features = generativeFeatures
  const feature = features.find(f => f.slug === location.pathname.split('/')[2])
  const featureMessage =
    (feature?.prompt && feature?.prompt(_user_name('there'))) ||
    `Hi ${_user_name('there')}! How can I help you today?`

  const featureTitle = feature?.title || 'Chat'
  const featureSubtitle =
    feature?.subtitle ||
    'Chat with Remble’s Well-Being Assistant about anything.'
  const featureIcon = feature?.icon || 'bi bi-chat-dots'
  const featureTips = feature?.tips || ''

  // Default messages
  let defaultMessages = [
    // Start with a welcome message
    {
      role: 'assistant',
      content: featureMessage
    }
  ]

  // State
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showTips, setShowTips] = useState(false)
  const [showTerms, setShowTerms] = useState(false)
  const [showExample, setShowExample] = useState(false)
  const [responseId, setResponseId] = useState(0)
  const [message, setMessage] = useState('')
  const [messages, setMessages] = useState(defaultMessages)
  const [hasInitialMessage, setHasInitialMessage] = useState(false)
  const [, dispatchResponse] = useReducer(responseReducer, [])
  const [backgroundSearchResult, setBackgroundSearchResult] = useState()
  const [rating, setRating] = useState(0)

  // This is used to identify the chat thread in the Remble API
  const threadId = useRef(null)

  /**
   * Takes a message response from the assistant and does a background search for the user
   * @param {String} message
   */
  const handleBackgroundSearch = async message => {
    try {
      const result = await getTermOrSearchResults(message)

      // Filter out any results that shouldn't be shown in the search
      let filteredResults = result?.results?.filter(
        result => LMSKeys[result.post_type]?.showInChat
      )

      // Randomize the results
      if (filteredResults) {
        filteredResults.sort(() => Math.random() - 0.5)
      }

      // Slice the results to show only the first 10
      if (filteredResults?.length > 10) {
        filteredResults = filteredResults.slice(0, 10)
      }

      setBackgroundSearchResult({ term: result.term, results: filteredResults })
    } catch (error) {
      // Fail silently
      console.warn('handleBackgroundSearch failed', error)
    }
  }

  /**
   * Helper function to update the chat messages and reset the response stream
   * @param {String} response The chunked streaming response from the API
   */
  const updateThread = useCallback(response => {
    // If we don't have a response or we aren't rendering, return
    if (!response) return

    // Increment the queue size
    _queueSize++

    // Create the message chunk
    const messageChunk = {
      role: 'assistant',
      content: response
    }

    // Add the response to the list of messages
    _chatMessages = [..._chatMessages, messageChunk]
    setMessages(_chatMessages)
  }, [])

  // const speak = useCallback(
  //   async message => {
  //     // Return if we don't have a message or the audio is disabled
  //     if (!message || !audioEnabled) return

  //     // Generate the audio source
  //     const audioSrc = `${app.api.url}/v1/chat/say?prompt=${encodeURIComponent(
  //       message
  //     )}&user_id=${user.id}`

  //     const context = new AudioContext()
  //     const source = context.createBufferSource()
  //     const audioBuffer = await fetch(audioSrc)
  //       .then(res => res.arrayBuffer())
  //       .then(ArrayBuffer => context.decodeAudioData(ArrayBuffer))

  //     source.buffer = audioBuffer
  //     source.connect(context.destination)
  //     source.start()
  //   },
  //   [user, audioEnabled]
  // )

  /**
   * Helper function to update the chat messages and reset the response stream
   * @param {String} response The completed response from the API
   */
  const updateAndResetThread = useCallback(
    response => {
      // If we don't have a response or we aren't rendering, return
      if (!response) return

      // Update the chat messages
      updateThread(response)

      // Handle a background search, if applicable
      const searchContext = _lastRequest // + ' ' + response
      handleBackgroundSearch(searchContext)

      // Speak the full response
      // speak(_lastFullTextResponse)

      // Reset the response stream
      dispatchResponse({ event: 'reset' })
      _latestTextResponse = ''
      _queueSize = 1

      // Set the loading state
      setIsLoading(false)
    },
    [updateThread]
  )

  /**
   * Helper function to handle the event stream
   * @param {Object} event The event data from the API
   * @param {String} event.data The event data
   * @param {String} event.id The event ID
   * @param {String} event.event The event type
   */
  const handleEventMessage = useCallback(
    event => {
      // Set the delay
      const delay = messageDelay * _queueSize

      // Make copy of the current message, since we will be updating it
      let chunk = '' + _latestTextResponse
      let eventData

      try {
        eventData = JSON.parse(event.data)
      } catch (error) {
        console.error('Error parsing event data:', error)
        return
      }

      // Clear the event source timeout
      clearTimeout(_eventSourceTimeout)

      // Check if the event is a final response, by looking for `[DONE]` in the content
      if (eventData?.data?.includes(STREAM_COMPLETE)) {
        // Set a timeout to update the chat messages
        setTimeout(() => {
          // Update the chat messages
          updateAndResetThread(chunk)
        }, delay)

        // Close the event source
        event.target.close()
        _eventSource = null

        setResponseId(eventData?.id)
        return
      } else {
        // TODO: Handle other event types (e.g. `SSE_EVENT_TYPE_ROLE`)
        if (eventData?.event === SSE_EVENT_TYPE_MESSAGE) {
          // Update the pending response, if it is content
          dispatchResponse(eventData)

          // Update the latest text response
          _latestTextResponse += eventData?.data

          // Update the latest full text response
          // _lastFullTextResponse += eventData?.data

          // If the data includes a double line break, split it into separate messages
          if (eventData?.data?.includes('\n\n')) {
            // Update the chunk
            chunk = _latestTextResponse

            // Reset the latest text response
            _latestTextResponse = ''

            // Set a timeout to update the chat messages
            setTimeout(() => {
              // Update the chat messages
              updateThread(chunk)
            }, delay)
          }
        }
      }

      // Add a timeout to close the event source and reset the response stream, if we don't get a response in time
      _eventSourceTimeout = setTimeout(() => {
        // Close the event source
        event.target.close()
        _eventSource = null

        // Reset the response stream
        updateAndResetThread(chunk)
      }, _eventSourceTimeoutDelay)
    },
    [updateAndResetThread, updateThread]
  )

  /**
   * Helper function to get the chat event source
   * @param {Array<Object>} chatMessages The messages to send to the server
   * @returns {Object} The event source
   */
  const handleCreateEventSource = useCallback(
    async chatMessages => {
      // If we don't have a query or if we're already generating, return
      if (!chatMessages.length || _eventSource) return

      // If we don't have a thread ID, create one
      if (!threadId.current) {
        // Generate a unique thread ID for this chat using a UUID
        // This is used to identify the chat thread in the Remble API
        try {
          threadId.current = crypto.randomUUID()
        } catch (err) {
          // Fallback to CryptoJS if self.crypto.randomUUID() is not available
          threadId.current = CryptoJS.lib.WordArray.random(128 / 8).toString(
            CryptoJS.enc.Hex
          )
        }
      }

      // Create the filter words
      const filterWords = [_user_name()]

      // Create the event source
      const eventSource = await getChatEventSource(
        chatMessages,
        threadId.current,
        filterWords
      )

      // Add the event listeners
      eventSource.onmessage = handleEventMessage
      eventSource.onerror = event => {
        // Make copy of the current message, since we will be updating it
        const chunk = '' + _latestTextResponse

        console.error('Error with event source:', event, chunk)
        event.target.close()
        updateAndResetThread(chunk)

        // Clear the timeout
        clearTimeout(_eventSourceTimeout)
      }
      eventSource.onopen = event => {
        // If we have an event source, log it
        if (_eventSource) return event.target.close()

        // Create reference to the event source
        _eventSource = event.target
      }

      // Return the event source
      return eventSource
    },
    [handleEventMessage, updateAndResetThread, _user_name]
  )

  /**
   * Clears the chat history back to the initial state
   */
  const clearChat = () => {
    // Reset the thread ID
    threadId.current = null

    // Reset the latest text response
    // _lastFullTextResponse = ''

    // Reset the chat messages
    setMessages([
      {
        role: 'assistant',
        content: featureMessage
      }
    ])
  }

  /**
   * Handle Submit
   */
  const handleSubmit = useCallback(
    async query => {
      // Return early if we're loading or the message is too short
      if (isLoading || query.length < chatMinLength) return

      // Update the loading state and error
      setIsLoading(true)
      setError('')
      setBackgroundSearchResult(null)

      // Update the last request
      _lastRequest = query

      // Create the message chunk
      const messageChunk = {
        role: 'user',
        content: query
      }

      // Add the user's message to the list of messages
      const updatedMessages = [...messages, messageChunk]
      setMessages(updatedMessages)
      setMessage('')

      // Update the chat messages
      _chatMessages = updatedMessages

      // Log the event
      saveUserEvent(EVENT_CATEGORY_CHAT, EVENT_ACTION_CHAT)

      // Create the event source
      handleCreateEventSource(updatedMessages)

      // Scroll to the bottom of the page
      setTimeout(() => {
        window.scrollTo(0, document.body.scrollHeight)
      }, 0)
    },
    [handleCreateEventSource, isLoading, messages]
  )

  // Helper function to check if we should show the actions
  const shouldShowActions = (messages, message, index) =>
    !isLoading &&
    message.role === 'assistant' &&
    messages.length > 1 &&
    index === messages.length - 1

  // Utility and Cleanup effect
  useEffect(() => {
    // Scroll to the top of the page
    window.scrollTo(0, 0)

    // Log the page view
    saveUserEvent(EVENT_CATEGORY_CHAT, EVENT_ACTION_PAGEVIEW, location.pathname)

    return () => {
      // Cleanup the event source on unmount
      if (_eventSource) _eventSource.close()

      // Clear the timeout
      if (_eventSourceTimeout) clearTimeout(_eventSourceTimeout)

      // Reset the thread ID
      threadId.current = null
    }
  }, [location.pathname, location.search])

  // Main effect
  useEffect(() => {
    // Update the return path and header
    setReturnPath(ROUTE_TODAY)
    setHeaderTitle('Chat')

    // If we're still loading, return
    if (termsLoading) return

    // If loading is complete and the terms have not been accepted, show the modal
    if (!termsAccepted) return setShowTerms(true)

    // If we've already sent an initial message or we don't have one, return
    if (hasInitialMessage || !location.search || messages.length > 1) return

    // If we passed an encoded message in the URL, add it to the default messages
    const encodedMessage = location.search.split('message=')[1]

    if (encodedMessage) {
      // Base64 decode the message
      const decodedMessage = Buffer.from(encodedMessage, 'base64').toString(
        'utf-8'
      )

      // Set the initial message flag
      setHasInitialMessage(true)

      // Update the latest text response
      setMessage(decodedMessage)

      // Handle the submit
      handleSubmit(decodedMessage)
    }
  }, [
    termsLoading,
    termsAccepted,
    location.search,
    handleSubmit,
    messages.length,
    hasInitialMessage,
    setHeaderTitle,
    setReturnPath
  ])

  return (
    <Loading isLoaded={termsLoading}>
      <Container className='chat-container'>
        <div className='no-print intro'>
          <i className={`bi bi-${featureIcon}`} />
          <h1 className='mb-0 title'>{featureTitle}</h1>
          <p className='muted'>
            <em>{featureSubtitle}</em>
            {featureTips && (
              <span
                className='tips-link green text-decoration-underline mx-2 cursor-pointer'
                onClick={_ => setShowTips(true)}
              >
                Tips
              </span>
            )}
          </p>
        </div>

        <Row>
          <Col xs={12} className='no-print'>
            {/* Show messages */}
            <div className='chat-messages'>
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={cn('chat-message', {
                    [message.role]: message.role
                  })}
                >
                  <div
                    className={cn('chat-message-avatar', {
                      [message.role]: message.role
                    })}
                  >
                    {message.role === 'assistant' ? (
                      <>
                        <img
                          src={rating > 0 ? RembleAvatarGolden : RembleAvatar}
                          alt=''
                          width='100%'
                        />
                      </>
                    ) : (
                      <>
                        {/* Get the first letter of the user's name */}
                        {_user_name().charAt(0).toUpperCase() || 'U'}
                      </>
                    )}
                  </div>
                  <div className='chat-message-content'>
                    <ReactMarkdown
                      children={message.content}
                      remarkPlugins={[remarkGfm]}
                    />
                  </div>
                  {/* Show actions or show the example? */}
                  {shouldShowActions(messages, message, index) ? (
                    <>
                      <ResponseRating
                        setRating={setRating}
                        responseId={responseId}
                      />

                      {/* Should we show a search expansion? */}
                      {backgroundSearchResult?.results?.length > 0 && (
                        <ChatSearchExpansion
                          searchResult={backgroundSearchResult}
                        />
                      )}
                    </>
                  ) : (
                    <div>
                      {/* Do we have an example? */}
                      {feature.example &&
                        messages.length <= defaultMessages.length && (
                          <>
                            {!showExample ? (
                              <Button
                                variant='text-link'
                                className='show-example-button muted'
                                onClick={_ => setShowExample(true)}
                              >
                                See example
                              </Button>
                            ) : (
                              <div className='chat-message-content example'>
                                <ReactMarkdown children={feature.example} />
                              </div>
                            )}
                          </>
                        )}
                    </div>
                  )}
                </div>
              ))}

              {/* Show the loading indicator */}
              {isLoading && (
                <div className='chat-message assistant loading'>
                  <div className='chat-message-avatar assistant'>
                    <img
                      src={rating > 0 ? RembleAvatarGolden : RembleAvatar}
                      alt=''
                      width='100%'
                    />
                  </div>
                  <div className='chat-message-content'>
                    <span className='bubble'>
                      <span className='dot'></span>
                      <span className='dot'></span>
                      <span className='dot'></span>
                      <span className='dot'></span>
                    </span>
                  </div>
                  <span className='visually-hidden'>
                    RembleBot is typing...
                  </span>
                </div>
              )}

              {error && (
                <Alert
                  variant='warning'
                  onClose={_ => setError('')}
                  dismissible
                >
                  {error}
                </Alert>
              )}
            </div>
            <div className='chat-message-input'>
              {/* Reset Chat - only show if we have messages other than the default */}
              {messages.length > defaultMessages.length && (
                <Button
                  variant='light'
                  className='reset-chat-button'
                  onClick={_ => clearChat()}
                  disabled={isLoading}
                  title='Reset Chat'
                >
                  <i className='bi bi-trash3'></i>
                </Button>
              )}
              <InputGroup className='chat-message-input-group'>
                <Form.Control
                  className='message-input'
                  name='message'
                  autoComplete='off'
                  id='message-field'
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  onKeyDown={e => {
                    // Search on enter, but only if the user is NOT holding down shift, we aren't loading, and the query is long enough
                    if (e.key === 'Enter') handleSubmit(e.target.value)
                  }}
                />

                <Button
                  className='chat-message-input-group'
                  onClick={_ => handleSubmit(message)}
                  title='Send Message'
                >
                  {isLoading ? (
                    <>
                      <Spinner
                        as='span'
                        animation='border'
                        size='sm'
                        role='status'
                        aria-hidden='true'
                        variant='light'
                        className='mx-2'
                      />
                      <span className='visually-hidden'>Loading...</span>
                    </>
                  ) : (
                    <i className='bi bi-send'></i>
                  )}
                </Button>
              </InputGroup>
            </div>
          </Col>
        </Row>

        {isLargeScreen ? (
          <>
            <Modal
              fullscreen='true'
              show={showTips}
              onHide={_ => setShowTips(false)}
              className='tips-modal'
            >
              <Modal.Header closeButton>
                <Modal.Title>Tips</Modal.Title>
              </Modal.Header>
              <Modal.Body>{featureTips && parse(featureTips)}</Modal.Body>
              <Modal.Footer>
                <Button variant='secondary' onClick={_ => setShowTips(false)}>
                  Close
                </Button>
              </Modal.Footer>
            </Modal>

            <Modal
              show={showTerms && !termsAccepted}
              onHide={_ => termsAccepted && setShowTerms(false)}
              className='terms-modal'
            >
              <Modal.Header>
                <Modal.Title>Well-Being Assistant Terms of Use</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Terms setAccepted={setTermsAccepted} />
              </Modal.Body>
            </Modal>
          </>
        ) : (
          <>
            <Drawer
              duration={250}
              hideScrollbars={true}
              isVisible={showTips}
              onClose={_ => setShowTips(false)}
            >
              <Row className='pb-5'>
                <Col xs={12} className='px-4'>
                  <h3 className='mt-3'>Tips</h3>
                  {featureTips && parse(featureTips)}
                </Col>
                <Col xs={12} className='mt-4 mb-5 text-end px-4 pb-5'>
                  <Button
                    variant='primary'
                    className='w-100'
                    onClick={_ => {
                      setShowTips(false)
                    }}
                  >
                    Done
                  </Button>
                </Col>
              </Row>
            </Drawer>
            <Drawer
              duration={250}
              hideScrollbars={true}
              isVisible={showTerms && !termsAccepted}
              onClose={_ => termsAccepted && setShowTerms(false)}
            >
              <Terms setAccepted={setTermsAccepted} />
            </Drawer>
          </>
        )}
      </Container>
    </Loading>
  )
}
